// frontend/src/PasswordReset.tsx - Password Reset Confirmation Component

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from './apiConfig';
import logoImage from './assets/logo.png';

interface PasswordResetProps {
  token: string;
  onSuccess: () => void;
}

const PasswordReset: React.FC<PasswordResetProps> = ({ token, onSuccess }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);

  // Validate password strength
  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];
    
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    if (!/\d/.test(password)) {
      errors.push('Password must contain at least one digit');
    }
    if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)) {
      errors.push('Password must contain at least one special character');
    }
    
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setPasswordErrors([]);

    // Validate password
    const validationErrors = validatePassword(newPassword);
    if (validationErrors.length > 0) {
      setPasswordErrors(validationErrors);
      return;
    }

    // Check password confirmation
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      await axios.post(`${API_URL}/api/auth/password-reset-confirm`, {
        token: token,
        new_password: newPassword
      });
      
      onSuccess();
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to reset password. The link may be expired or invalid.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-brand-gradient-screen flex items-center justify-center p-md">
      <div className="card w-full" style={{ maxWidth: '480px' }}>
        <div className="text-center mb-xl">
          <div className="flex justify-center mb-lg p-lg" style={{
            backgroundColor: 'white',
            borderRadius: 'var(--radius-lg)'
          }}>
            <img 
              src={logoImage} 
              alt="EvaluTool Logo" 
              className="logo-lg"
            />
          </div>
          <h1 className="mb-sm" style={{
            background: 'linear-gradient(45deg, #2563eb, #059669)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            Reset Your Password
          </h1>
          <p className="text-secondary">
            Enter your new password below
          </p>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="newPassword" className="form-label">
              New Password
            </label>
            <input
              id="newPassword"
              type="password"
              required
              className="input"
              placeholder="Enter your new password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={isLoading}
            />
            {passwordErrors.length > 0 && (
              <div style={{ marginTop: '8px' }}>
                {passwordErrors.map((error, index) => (
                  <div key={index} style={{ 
                    color: '#ef4444', 
                    fontSize: '14px',
                    marginTop: '4px'
                  }}>
                    • {error}
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">
              Confirm New Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              required
              className="input"
              placeholder="Confirm your new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isLoading}
            />
          </div>

          {error && (
            <div className="card-error mb-lg">
              <h4 className="text-primary font-medium mb-xs">
                Reset Error
              </h4>
              <p className="text-secondary">
                {error}
              </p>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="btn btn-primary w-full"
          >
            {isLoading ? (
              <>
                <div className="loading-spinner"></div>
                Resetting Password...
              </>
            ) : (
              'Reset Password'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PasswordReset;